
/**
 * Timezone utilities for MasterJi Frontend
 * Handles timezone detection, conversion, and display
 */

export interface TimezoneInfo {
  timezone: string;
  currentTime: string;
  utcOffset: string;
  utcOffsetHours: number;
  isDst: boolean;
}

/**
 * Detect user's timezone automatically
 */
export const detectUserTimezone = (): string => {
  try {
    // Use Intl.DateTimeFormat to detect timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return timezone;
  } catch (_error) {
    console.warn('Failed to detect timezone:', error);
    return 'Asia/Kolkata'; // Default fallback
  }
};

/**
 * Get timezone offset in minutes
 */
export const getTimezoneOffset = (): number => {
  return new Date().getTimezoneOffset();
};

/**
 * Format date/time in user's timezone
 */
export const formatDateTimeInTimezone = (
  dateString: string | Date,
  timezone?: string,
  options?: Intl.DateTimeFormatOptions
): string => {
  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
      ...options
    };

    if (timezone) {
      return new Intl.DateTimeFormat('en-US', {
        ...defaultOptions,
        timeZone: timezone
      }).format(date);
    }

    return new Intl.DateTimeFormat('en-US', defaultOptions).format(date);
  } catch (_error) {
    console.warn('Failed to format date in timezone:', error);
    return new Date(dateString).toLocaleString();
  }
};

/**
 * Convert appointment time to user's timezone
 */
export const convertToUserTimezone = (
  appointmentTime: string,
  fromTimezone: string,
  toTimezone?: string
): string => {
  try {
    const targetTimezone = toTimezone || detectUserTimezone();
    
    // Create date in source timezone
    const date = new Date(appointmentTime);
    
    // Format in target timezone
    return formatDateTimeInTimezone(date, targetTimezone, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  } catch (_error) {
    console.warn('Failed to convert timezone:', error);
    return appointmentTime;
  }
};

/**
 * Get timezone information
 */
export const getTimezoneInfo = (timezone?: string): TimezoneInfo => {
  const targetTimezone = timezone || detectUserTimezone();
  const now = new Date();
  
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: targetTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });

    const parts = formatter.formatToParts(now);
    const timeZoneName = parts.find(part => part.type === 'timeZoneName')?.value || '';
    const utcOffset = getTimezoneOffset();
    
    return {
      timezone: targetTimezone,
      currentTime: formatter.format(now),
      utcOffset: `UTC${utcOffset > 0 ? '-' : '+'}${Math.abs(utcOffset / 60).toString().padStart(2, '0')}:${Math.abs(utcOffset % 60).toString().padStart(2, '0')}`,
      utcOffsetHours: -utcOffset / 60, // Note: getTimezoneOffset returns negative values for positive offsets
      isDst: isDaylightSavingTime(now, targetTimezone)
    };
  } catch (_error) {
    console.warn('Failed to get timezone info:', error);
    return {
      timezone: targetTimezone,
      currentTime: now.toLocaleString(),
      utcOffset: '+05:30',
      utcOffsetHours: 5.5,
      isDst: false
    };
  }
};

/**
 * Check if daylight saving time is active
 */
const isDaylightSavingTime = (date: Date, timezone: string): boolean => {
  try {
    const jan = new Date(date.getFullYear(), 0, 1);
    const jul = new Date(date.getFullYear(), 6, 1);
    
    const janOffset = getTimezoneOffsetForDate(jan, timezone);
    const julOffset = getTimezoneOffsetForDate(jul, timezone);
    
    return janOffset !== julOffset;
  } catch (_error) {
    return false;
  }
};

/**
 * Get timezone offset for a specific date
 */
const getTimezoneOffsetForDate = (date: Date, timezone: string): number => {
  try {
    const utc1 = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const utc2 = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    return (utc2.getTime() - utc1.getTime()) / (1000 * 60);
  } catch (_error) {
    return 0;
  }
};

/**
 * Get commonly used timezones for the application
 */
export const getCommonTimezones = (): Array<{ value: string; label: string; offset: string }> => {
  return [
    { value: 'Asia/Kolkata', label: 'India (IST)', offset: '+05:30' },
    { value: 'Asia/Dubai', label: 'UAE (GST)', offset: '+04:00' },
    { value: 'Asia/Singapore', label: 'Singapore (SGT)', offset: '+08:00' },
    { value: 'Asia/Tokyo', label: 'Japan (JST)', offset: '+09:00' },
    { value: 'Asia/Shanghai', label: 'China (CST)', offset: '+08:00' },
    { value: 'Europe/London', label: 'UK (GMT/BST)', offset: '+00:00/+01:00' },
    { value: 'Europe/Paris', label: 'France (CET/CEST)', offset: '+01:00/+02:00' },
    { value: 'America/New_York', label: 'US East (EST/EDT)', offset: '-05:00/-04:00' },
    { value: 'America/Los_Angeles', label: 'US West (PST/PDT)', offset: '-08:00/-07:00' },
    { value: 'America/Chicago', label: 'US Central (CST/CDT)', offset: '-06:00/-05:00' },
    { value: 'Australia/Sydney', label: 'Australia (AEST/AEDT)', offset: '+10:00/+11:00' },
    { value: 'UTC', label: 'UTC', offset: '+00:00' }
  ];
};

/**
 * Check if a time is within business hours
 */
export const isBusinessHours = (
  date: Date,
  timezone?: string,
  businessStart: number = 9,
  businessEnd: number = 18
): boolean => {
  try {
    const targetTimezone = timezone || detectUserTimezone();
    const localDate = new Date(date.toLocaleString('en-US', { timeZone: targetTimezone }));
    
    // Check if it's a weekday (Monday = 1, Sunday = 0)
    const dayOfWeek = localDate.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) { // Weekend
      return false;
    }
    
    // Check if it's within business hours
    const hour = localDate.getHours();
    return hour >= businessStart && hour < businessEnd;
  } catch (_error) {
    console.warn('Failed to check business hours:', error);
    return true; // Default to allowing if we can't determine
  }
};

/**
 * Get timezone-aware headers for API requests
 */
export const getTimezoneHeaders = (): Record<string, string> => {
  return {
    'x-user-timezone': detectUserTimezone(),
    'x-timezone-offset': getTimezoneOffset().toString()
  };
};
